import React, { createContext, useContext, useState, useCallback } from 'react'

/**
 * Context for global tour state
 * Centralizes state and operations related to tours
 */
const TourContext = createContext()

/**
 * Custom hook to use tour context
 */
export const useTourContext = () => {
  const context = useContext(TourContext)
  if (!context) {
    throw new Error('useTourContext must be used within TourProvider')
  }
  return context
}

/**
 * Tour context provider
 */
export const TourProvider = ({ children }) => {
  // Global tour state
  const [tourState, setTourState] = useState({
    selectedTour: null,
    pricing: null,
    validation: null,
    formData: {},
    notifications: [],
    loading: false,
    error: null
  })

  // Actions to modify state
  const updateTourState = useCallback((updates) => {
    setTourState(prev => ({ ...prev, ...updates }))
  }, [])

  const setSelectedTour = useCallback((tour) => {
    updateTourState({ selectedTour: tour })
  }, [updateTourState])

  const setPricing = useCallback((pricing) => {
    updateTourState({ pricing })
  }, [updateTourState])

  const setValidation = useCallback((validation) => {
    updateTourState({ validation })
  }, [updateTourState])

  const setFormData = useCallback((formData) => {
    updateTourState({ formData })
  }, [updateTourState])

  const addNotification = useCallback((notification) => {
    setTourState(prev => ({
      ...prev,
      notifications: [...prev.notifications, { ...notification, id: Date.now() }]
    }))
  }, [])

  const removeNotification = useCallback((notificationId) => {
    setTourState(prev => ({
      ...prev,
      notifications: prev.notifications.filter(n => n.id !== notificationId)
    }))
  }, [])

  const clearNotifications = useCallback(() => {
    updateTourState({ notifications: [] })
  }, [updateTourState])

  const setLoading = useCallback((loading) => {
    updateTourState({ loading })
  }, [updateTourState])

  const setError = useCallback((error) => {
    updateTourState({ error })
  }, [updateTourState])

  const clearError = useCallback(() => {
    updateTourState({ error: null })
  }, [updateTourState])

  // Function to reset state
  const resetTourState = useCallback(() => {
    setTourState({
      selectedTour: null,
      pricing: null,
      validation: null,
      formData: {},
      notifications: [],
      loading: false,
      error: null
    })
  }, [])

  const value = {
    tourState,
    setTourState: updateTourState,
    setSelectedTour,
    setPricing,
    setValidation,
    setFormData,
    addNotification,
    removeNotification,
    clearNotifications,
    setLoading,
    setError,
    clearError,
    resetTourState
  }

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  )
}

export { TourContext } 


// WEBPACK FOOTER //
// ./src/features/tours/contexts/TourContext.js